<script setup lang="ts">
import CombineRollbackForm from '@/components/DailyReportEdit/CombineRollback/CombineRollbackForm.vue';
import getDeliveryPlanVehicleInformation from '@/types/responses/getDeliveryPlanVehicleInformation';
import AlterReturningForm from '@/components/DailyReportEdit/AlterReturning/AlterReturningForm.vue';
import AlterLeavingForm from '@/components/DailyReportEdit/AlterLeaving/AlterLeavingForm.vue';
import SplitForm from '@/components/DailyReportEdit/Split/SplitForm.vue';
import MergeForm from '@/components/DailyReportEdit/Merge/MergeForm.vue';
import RecreateForm from '@/components/DailyReportEdit/Recreate/RecreateForm.vue';
import CombineForm from '@/components/DailyReportEdit/Combine/CombineForm.vue';
import { useAuth } from '@/services/user/Auth0UserProvider';
import Api from '@/services/api/ApiServiceFabrick';
import { useRoute, useRouter } from 'vue-router';
import { ref, Ref, onMounted } from 'vue';
import {
  PATH_SUFFIX_ALTER_LEAVING,
  PATH_SUFFIX_ALTER_RETURNING,
  PATH_SUFFIX_DIVIDE,
  PATH_SUFFIX_MERGE,
  PATH_SUFFIX_RECREATE,
  PATH_SUFFIX_COMBINE,
  PATH_SUFFIX_COMBINE_ROLLBACK,
} from '@/services/consts/daily_report';
import i18n from '@/lang/i18n';

const props = defineProps<{
  switchContentLoading: (isLoading: boolean) => void
  showFlash: (message: string, type: string, fadeout: boolean) => void
}>();

const { loginWithRedirect } = useAuth();
const route = useRoute();
const router = useRouter();
const path = route.path;
const splitPath = path.split('/');
const pathSuffix = splitPath[splitPath.length - 1];
const deliveryPlanId: string = route.params.id as string;
const deliveryPlan = ref({} as getDeliveryPlanVehicleInformation);
const isDisplaySplit: Ref<boolean> = ref(false);
const isDisplayMerge: Ref<boolean> = ref(false);
const isDisplayAlterReturning: Ref<boolean> = ref(false);
const isDisplayAlterLeaving: Ref<boolean> = ref(false);
const isDisplayRecreate: Ref<boolean> = ref(false);
const isDisplayCombine: Ref<boolean> = ref(false);
const isDisplayCombineRollback: Ref<boolean> = ref(false);

onMounted(() => {
  props.switchContentLoading(true);
  setDocumentTitle();
  getDeliveryPlan();
});

function getDeliveryPlan() {
  Api().getDeliveryPlan(deliveryPlanId).then((data: getDeliveryPlanVehicleInformation) => {
    deliveryPlan.value = data;
    isViewableThisPage(data.editable_actions);
  }).catch((error) => {
    errorHandler(error);
  }).finally(() => {
    if (pathSuffix != PATH_SUFFIX_MERGE) {
      props.switchContentLoading(false);
    }
  });
}

function errorHandler(error: any) {
  const res = error.response
  switch (res.status) {
    case 401:
      loginWithRedirect({ appState: { targetUrl: location.pathname } })
      break
    case 403:
      location.reload()
      break
  }
}

function setDocumentTitle() {
  switch (pathSuffix) {
    case PATH_SUFFIX_ALTER_RETURNING:
      document.title = i18n.global.t('pages.dailyReports.alterLeavingOrReturning.returningTitle');
      break;
    case PATH_SUFFIX_ALTER_LEAVING:
      document.title = i18n.global.t('pages.dailyReports.alterLeavingOrReturning.leavingTitle');
      break;
    case PATH_SUFFIX_DIVIDE:
      document.title = i18n.global.t('pages.dailyReports.splitForm.title');
      break;
    case PATH_SUFFIX_MERGE:
      document.title = i18n.global.t('pages.dailyReports.mergeForm.title');
      break;
    case PATH_SUFFIX_RECREATE:
      document.title = i18n.global.t('pages.dailyReports.recreateForm.title');
      break;
    case PATH_SUFFIX_COMBINE:
      document.title = i18n.global.t('pages.dailyReports.combineForm.title');
      break;
    case PATH_SUFFIX_COMBINE_ROLLBACK:
      document.title = i18n.global.t('pages.dailyReports.combineRollbackForm.title');
      break;
  }
}

function isViewableThisPage(actions: getDeliveryPlanVehicleInformation['editable_actions']) {
  switch (pathSuffix) {
    case PATH_SUFFIX_ALTER_RETURNING:
      if (!actions?.alter_returning?.available) {
        router.push({ name: "DailyReport" })
      }
      isDisplayAlterReturning.value = true;
      break;
    case PATH_SUFFIX_ALTER_LEAVING:
      if (!actions?.alter_leaving?.available) {
        router.push({ name: "DailyReport" })
      }
      isDisplayAlterLeaving.value = true;
      break;
    case PATH_SUFFIX_DIVIDE:
      if (!actions?.divide?.available) {
        router.push({ name: "DailyReport" })
      }
      isDisplaySplit.value = true;
      break;
    case PATH_SUFFIX_MERGE:
      if (!actions?.merge?.available) {
        router.push({ name: "DailyReport" })
      }
      isDisplayMerge.value = true;
      break;
    case PATH_SUFFIX_RECREATE:
      if (!actions?.recreate?.available) {
        router.push({ name: "DailyReport" })
      }
      isDisplayRecreate.value = true;
      break;
    case PATH_SUFFIX_COMBINE:
      if (!actions?.combine?.available || !deliveryPlan.value.driver) {
        return router.push({ name: "DailyReport" })
      }
      isDisplayCombine.value = true;
      break;
    case PATH_SUFFIX_COMBINE_ROLLBACK:
      if (!actions?.combine_rollback?.available) {
        router.push({ name: "DailyReport" })
      }
      isDisplayCombineRollback.value = true;
      break;
  }
}
</script>
<template>
  <SplitForm
    v-if="isDisplaySplit"
    :deliveryPlanId="deliveryPlanId"
    :deliveryPlan="deliveryPlan"
    :showFlash="props.showFlash"
  />
  <MergeForm
    v-else-if="isDisplayMerge"
    :deliveryPlanId="deliveryPlanId"
    :deliveryPlan="deliveryPlan"
    :switchContentLoading="props.switchContentLoading"
    :showFlash="props.showFlash"
  />
  <AlterLeavingForm
    v-else-if="isDisplayAlterLeaving"
    :deliveryPlan="deliveryPlan"
    :showFlash="props.showFlash"
  />
  <AlterReturningForm
    v-else-if="isDisplayAlterReturning"
    :deliveryPlan="deliveryPlan"
    :showFlash="props.showFlash"
  />
  <RecreateForm
    v-else-if="isDisplayRecreate"
    :deliveryPlan="deliveryPlan"
    :showFlash="props.showFlash"
  />
  <CombineForm
    v-else-if="isDisplayCombine"
    :deliveryPlan="deliveryPlan"
    :showFlash="props.showFlash"
  />
  <CombineRollbackForm
    v-else-if="isDisplayCombineRollback"
    :deliveryPlan="deliveryPlan"
    :showFlash="props.showFlash"
  />
</template>
